import {
  MapContainer,
  TileLayer,
  GeoJSON,
  useMap,
  Marker,
  Popup,
} from "react-leaflet";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Badge,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import PanToolIcon from "@mui/icons-material/PanTool";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import L, { LatLngBounds } from "leaflet";
import { useEffect, useMemo, useState, useCallback } from "react";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import "leaflet/dist/leaflet.css";
import "./leaflet-custom.css";
import {
  useSeedDeliveryRegions,
  useUpdateSeedDeliveryRegions,
} from "../../services/useSeedDeliveryRegions";
import {
  useDeliveryRegionsInBounds,
  IMapBounds,
} from "../../services/useDeliveryRegions";
import { useDebounce } from "../../hooks/useDebounce";
import useSeed from "../../services/useSeed";
import { IDeliveryRegionsGeoJSONCollection } from "../../types/deliveryRegion";

// Fix para os ícones do Leaflet
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12.5, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Componente para capturar bounds e zoom do mapa
const MapBoundsTracker = ({
  onBoundsChange,
  onZoomChange,
}: {
  onBoundsChange: (bounds: IMapBounds | null) => void;
  onZoomChange: (zoom: number) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    const updateBounds = () => {
      const bounds = map.getBounds();
      if (bounds.isValid()) {
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      } else {
        onBoundsChange(null);
      }
    };

    const updateZoom = () => {
      const zoom = map.getZoom();
      onZoomChange(zoom);
    };

    const handleZoomEnd = () => {
      updateBounds();
      updateZoom();
    };

    // Atualiza bounds e zoom inicialmente
    updateBounds();
    updateZoom();

    // Listener para quando o mapa move ou faz zoom
    map.on("moveend", updateBounds);
    map.on("zoomend", handleZoomEnd);
    map.on("zoom", updateZoom);

    return () => {
      map.off("moveend", updateBounds);
      map.off("zoomend", handleZoomEnd);
      map.off("zoom", updateZoom);
    };
  }, [map, onBoundsChange, onZoomChange]);

  return null;
};

// Componente para ajustar o mapa para mostrar todas as regiões selecionadas
const FitBoundsToRegions = ({
  regions,
  shouldFit,
  onFitComplete,
}: {
  regions: IDeliveryRegionsGeoJSONCollection | undefined;
  shouldFit: boolean;
  onFitComplete: () => void;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!shouldFit || !regions || regions.features.length === 0) {
      return;
    }

    try {
      const bounds = new LatLngBounds([]);

      regions.features.forEach((feature) => {
        if (feature.geometry.type === "Polygon") {
          const coords = feature.geometry.coordinates as number[][][];
          coords.forEach((ring) => {
            ring.forEach((coord) => {
              if (Array.isArray(coord) && coord.length >= 2) {
                bounds.extend([coord[1] as number, coord[0] as number]);
              }
            });
          });
        } else if (feature.geometry.type === "MultiPolygon") {
          const coords = feature.geometry.coordinates as number[][][][];
          coords.forEach((polygon) => {
            polygon.forEach((ring) => {
              ring.forEach((coord) => {
                if (Array.isArray(coord) && coord.length >= 2) {
                  bounds.extend([coord[1] as number, coord[0] as number]);
                }
              });
            });
          });
        }
      });

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
        onFitComplete();
      }
    } catch (error) {
      console.error("Erro ao ajustar bounds do mapa:", error);
      onFitComplete();
    }
  }, [shouldFit, regions, map, onFitComplete]);

  return null;
};

type MapMode = "navigation" | "selection";

const DeliveryRegionsMap = () => {
  console.time("[DeliveryRegionsMap] Total render time");

  const { data: seedData } = useSeed();

  // Estado interno para modo do mapa e seleção
  const [mapMode, setMapMode] = useState<MapMode>("navigation");
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );

  // Estados para snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  // Estado para confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] =
    useState<GridRowSelectionModel>([]);

  // Estado para controlar fit bounds
  const [shouldFitBounds, setShouldFitBounds] = useState(false);

  const seedLatitude = seedData?.address?.latitude;
  const seedLongitude = seedData?.address?.longitude;
  const seedPosition: [number, number] | null =
    seedLatitude != null && seedLongitude != null
      ? [Number(seedLatitude), Number(seedLongitude)]
      : null;

  // Centraliza no ponto do endereço da seed, quando disponível.
  const center: [number, number] = seedData
    ? seedPosition || [-30.0346, -51.2177]
    : [-30.0346, -51.2177]; // Coordenadas padrão de Porto Alegre

  // Estado para bounds e zoom do mapa
  // Zoom inicial padrão é 12 (mesmo do MapContainer)
  const [mapBounds, setMapBounds] = useState<IMapBounds | null>(null);
  const [mapZoom, setMapZoom] = useState<number | null>(12);

  // Debounce dos bounds (300ms)
  const debouncedBounds = useDebounce(mapBounds, 300);
  // Debounce do zoom (menor delay, 100ms, pois zoom muda menos frequentemente)
  const debouncedZoom = useDebounce(mapZoom, 100);

  const { data: seedRegionsData, error: seedRegionsError } =
    useSeedDeliveryRegions();

  // Mutation para atualizar regiões
  const updateMutation = useUpdateSeedDeliveryRegions();

  // IDs originais salvos no backend
  const [originalRegionIds, setOriginalRegionIds] = useState<number[]>([]);

  // Carrega as regiões selecionadas quando os dados chegam do backend
  useEffect(() => {
    if (seedRegionsData) {
      setSelectionModel(seedRegionsData.region_ids);
      setOriginalRegionIds(seedRegionsData.region_ids);
      // Ajustar bounds quando carregar regiões pela primeira vez
      if (seedRegionsData.region_ids.length > 0) {
        setShouldFitBounds(true);
      }
    }
  }, [seedRegionsData]);

  // Detecta se há alterações não salvas
  const hasUnsavedChanges = useMemo(() => {
    const currentIds = new Set(selectionModel.map(Number));
    const originalIds = new Set(originalRegionIds);

    if (currentIds.size !== originalIds.size) return true;

    // Converte Set para Array para iterar
    const currentIdsArray = Array.from(currentIds);
    for (const id of currentIdsArray) {
      if (!originalIds.has(id)) return true;
    }

    return false;
  }, [selectionModel, originalRegionIds]);

  // Sempre usa carregamento incremental baseado em bounds do viewport e zoom
  const {
    data: allRegionsData,
    isLoading: isLoadingAllRegions,
    isFetching: isFetchingAllRegions,
    error: allRegionsError,
  } = useDeliveryRegionsInBounds(debouncedBounds, debouncedZoom);

  // Não bloqueia renderização do mapa enquanto carrega dados
  const error = seedRegionsError || allRegionsError;

  // Separa as regiões em selecionadas e não selecionadas
  const { selectedRegions, unselectedRegions } = useMemo(() => {
    console.time("[DeliveryRegionsMap] Processing regions");

    // Se não tiver dados carregados ainda, retorna apenas regiões selecionadas da seed (se disponível)
    if (!allRegionsData) {
      const result = {
        selectedRegions: seedRegionsData?.regions,
        unselectedRegions: undefined,
      };
      console.timeEnd("[DeliveryRegionsMap] Processing regions");
      return result;
    }

    // Usa selectionModel das props se disponível, senão usa seedRegionsData
    const selectedIds = new Set(
      selectionModel.length > 0
        ? selectionModel.map(Number)
        : seedRegionsData?.region_ids || [],
    );

    const selected: IDeliveryRegionsGeoJSONCollection = {
      type: "FeatureCollection",
      features: allRegionsData.features.filter((feature) =>
        selectedIds.has(feature.id),
      ),
    };

    const unselected: IDeliveryRegionsGeoJSONCollection = {
      type: "FeatureCollection",
      features: allRegionsData.features.filter(
        (feature) => !selectedIds.has(feature.id),
      ),
    };

    console.timeEnd("[DeliveryRegionsMap] Processing regions");
    console.log(
      `[DeliveryRegionsMap] Selected: ${selected.features.length}, Unselected: ${unselected.features.length}`,
    );

    return { selectedRegions: selected, unselectedRegions: unselected };
  }, [allRegionsData, seedRegionsData, selectionModel]);

  useEffect(() => {
    console.timeEnd("[DeliveryRegionsMap] Total render time");
  });

  // Handlers
  const handleMapModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: MapMode | null,
  ) => {
    if (newMode !== null) {
      setMapMode(newMode);
    }
  };

  const handleSave = useCallback(() => {
    const regionIds = selectionModel.map(Number);
    updateMutation.mutate(regionIds, {
      onSuccess: () => {
        setOriginalRegionIds(regionIds);
        setSnackbarSeverity("success");
        setSnackbarMessage("Regiões de entrega salvas com sucesso!");
        setSnackbarOpen(true);
      },
      onError: (error: any) => {
        setSnackbarSeverity("error");
        setSnackbarMessage(
          error?.message ||
            "Erro ao salvar regiões de entrega. Tente novamente.",
        );
        setSnackbarOpen(true);
      },
    });
  }, [selectionModel, updateMutation]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleConfirmDialogClose = (confirmed: boolean) => {
    if (confirmed) {
      setSelectionModel(pendingSelection);
    }
    setConfirmDialogOpen(false);
    setPendingSelection([]);
  };

  const handleFitBoundsComplete = useCallback(() => {
    setShouldFitBounds(false);
  }, []);

  // Handler para clique em região
  const handleRegionClick = useCallback(
    (regionId: number, e: L.LeafletMouseEvent) => {
      if (mapMode === "selection") {
        // Para evitar pan do mapa
        L.DomEvent.stopPropagation(e.originalEvent);

        const currentSelection = new Set(selectionModel.map(Number));
        const wasSelected = currentSelection.has(regionId);

        if (wasSelected) {
          // Remove da seleção
          currentSelection.delete(regionId);
        } else {
          // Adiciona à seleção
          currentSelection.add(regionId);
        }

        // Converte de volta para array
        const newSelection = Array.from(currentSelection);

        // Se está desmarcando todas, mostra confirm dialog
        if (newSelection.length === 0 && selectionModel.length > 0) {
          setPendingSelection(newSelection);
          setConfirmDialogOpen(true);
        } else {
          setSelectionModel(newSelection);
        }
      }
    },
    [mapMode, selectionModel],
  );

  // Função para adicionar popup e eventos a cada feature (modo navegação)
  const onEachFeatureNavigation = useCallback(
    (feature: any, layer: any) => {
      if (!feature.properties) return;

      const { nome, cidade } = feature.properties;
      const selectedIds = new Set(selectionModel.map(Number));
      const isSelected = selectedIds.has(feature.id);
      const status = isSelected ? "✓ Selecionada" : "Não selecionada";
      const statusColor = isSelected ? "#4CAF50" : "#666";

      layer.bindPopup(`
      <div style="padding: 8px;">
        <strong>${nome}</strong><br/>
        <span style="color: #666;">${cidade}</span><br/>
        <span style="color: ${statusColor}; font-weight: bold;">${status}</span>
      </div>
    `);
    },
    [selectionModel],
  );

  // Função para adicionar eventos a cada feature (modo seleção)
  const onEachFeatureSelection = useCallback(
    (feature: any, layer: any) => {
      if (!feature.properties) return;

      const { nome, cidade } = feature.properties;
      const selectedIds = new Set(selectionModel.map(Number));
      const isSelected = selectedIds.has(feature.id);

      // Tooltip para modo seleção
      layer.bindTooltip(`<strong>${nome}</strong><br/>${cidade}`, {
        sticky: true,
      });

      // Handler de clique
      const clickHandler = (e: L.LeafletMouseEvent) => {
        handleRegionClick(feature.id, e);
      };
      layer.on("click", clickHandler);

      // Hover com outline amarelo
      const mouseoverHandler = function (this: L.Path) {
        this.setStyle({
          color: "#FFD700",
          weight: 4,
        });
      };

      const mouseoutHandler = function (this: L.Path) {
        // Volta para o estilo original baseado se está selecionado ou não
        if (isSelected) {
          this.setStyle({
            color: "#2E7D32",
            weight: 3,
          });
        } else {
          this.setStyle({
            color: "#3388ff",
            weight: 1,
          });
        }
      };

      layer.on("mouseover", mouseoverHandler);
      layer.on("mouseout", mouseoutHandler);
    },
    [selectionModel, handleRegionClick],
  );

  // Seleciona a função apropriada baseada no modo do mapa
  const onEachFeature = useMemo(() => {
    return mapMode === "navigation"
      ? onEachFeatureNavigation
      : onEachFeatureSelection;
  }, [mapMode, onEachFeatureNavigation, onEachFeatureSelection]);

  // Mostra erro apenas se houver problema crítico
  if (error && !seedData) {
    return <Alert severity="error">Erro ao carregar dados da seed.</Alert>;
  }

  const hasSelectedRegions =
    selectedRegions && selectedRegions.features.length > 0;
  const hasUnselectedRegions =
    unselectedRegions && unselectedRegions.features.length > 0;

  // Estilo para regiões selecionadas (verde, mais opaco)
  const selectedStyle = () => {
    return {
      fillColor: "#4CAF50",
      fillOpacity: 0.6,
      color: "#2E7D32",
      weight: 3,
      cursor: mapMode === "selection" ? "pointer" : "grab",
    };
  };

  // Estilo para regiões não selecionadas (azul, mais transparente)
  const unselectedStyle = () => {
    return {
      fillColor: "#3388ff",
      fillOpacity: 0.2,
      color: "#3388ff",
      weight: 1,
      cursor: mapMode === "selection" ? "pointer" : "grab",
    };
  };

  const totalRegions =
    (selectedRegions?.features.length || 0) +
    (unselectedRegions?.features.length || 0);

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component="h2" variant="h6">
          Regiões de Entrega
        </Typography>

        {/* Badge com contador sempre visível */}
        <Badge
          badgeContent={selectionModel.length}
          color="primary"
          max={9999}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.875rem",
              height: "24px",
              minWidth: "24px",
              padding: "0 6px",
            },
          }}
        >
          <Chip
            icon={<CheckCircleIcon />}
            label="Selecionadas"
            color="primary"
            variant="outlined"
          />
        </Badge>
      </Box>

      {/* Chip de alterações não salvas */}
      {hasUnsavedChanges && (
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<EditIcon />}
            label="Você tem alterações não salvas"
            color="warning"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      )}

      {/* Toggle de modo do mapa com tooltips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Modo do Mapa:
        </Typography>
        <ToggleButtonGroup
          value={mapMode}
          exclusive
          onChange={handleMapModeChange}
          size="small"
        >
          <ToggleButton
            title="Modo Navegação: arraste o mapa para explorar diferentes áreas"
            value="navigation"
          >
            <PanToolIcon sx={{ mr: 1 }} fontSize="small" />
            Navegação
          </ToggleButton>
          <ToggleButton
            title="Modo Seleção: clique nas regiões para selecionar ou desmarcar"
            value="selection"
          >
            <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
            Seleção
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {allRegionsData
            ? `Total de regiões no viewport: ${totalRegions}`
            : "Carregando regiões no viewport..."}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#4CAF50",
                opacity: 0.6,
                border: "2px solid #2E7D32",
              }}
            />
            <Typography variant="body2">
              Selecionadas: {selectedRegions?.features.length || 0}
            </Typography>
          </Box>
          {hasUnselectedRegions && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: "#3388ff",
                  opacity: 0.2,
                  border: "1px solid #3388ff",
                }}
              />
              <Typography variant="body2">
                Não selecionadas: {unselectedRegions?.features.length || 0}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          height: 500,
          width: "100%",
          mt: 2,
          borderRadius: 1,
          overflow: "hidden",
          position: "relative",
          "& .leaflet-container": {
            height: "100%",
            width: "100%",
          },
        }}
      >
        {/* Overlay translúcido durante fetch (não intrusivo) */}
        {isFetchingAllRegions && !isLoadingAllRegions && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
              bgcolor: "rgba(255, 255, 255, 0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "50%",
                p: 1.5,
                boxShadow: 2,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          </Box>
        )}

        {/* Mensagem de erro não bloqueante */}
        {error ? (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 1000,
              bgcolor: "rgba(255, 255, 255, 0.95)",
              p: 1,
              borderRadius: 1,
              boxShadow: 2,
            }}
          >
            <Alert severity="warning" sx={{ py: 0 }}>
              Erro ao carregar regiões. Movendo o mapa pode resolver.
            </Alert>
          </Box>
        ) : null}

        <MapContainer
          center={center}
          zoom={12}
          minZoom={10}
          maxZoom={18}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Componente para rastrear bounds e zoom do mapa - sempre ativo */}
          <MapBoundsTracker
            onBoundsChange={setMapBounds}
            onZoomChange={setMapZoom}
          />

          {/* Componente para ajustar bounds ao carregar regiões selecionadas */}
          <FitBoundsToRegions
            regions={seedRegionsData?.regions}
            shouldFit={shouldFitBounds}
            onFitComplete={handleFitBoundsComplete}
          />

          {/* Renderiza regiões não selecionadas primeiro (layer de baixo) */}
          {hasUnselectedRegions && (
            <GeoJSON
              key={`unselected-${unselectedRegions.features.length}-${mapMode}-${selectionModel.join(",")}`}
              data={unselectedRegions as any}
              style={unselectedStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {/* Renderiza regiões selecionadas por cima */}
          {hasSelectedRegions && (
            <GeoJSON
              key={`selected-${selectedRegions.features.length}-${mapMode}-${selectionModel.join(",")}`}
              data={selectedRegions as any}
              style={selectedStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {seedData && seedPosition && (
            <Marker position={seedPosition}>
              <Popup>
                <strong>{seedData.name}</strong>
              </Popup>
            </Marker>
          )}

          {/* Não ajusta bounds automaticamente - deixa o usuário navegar livremente */}
          {/* {selectedRegions && <FitBounds data={selectedRegions} />} */}
        </MapContainer>
      </Box>

      {/* Informações e botão de salvar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectionModel.length > 0
            ? `${selectionModel.length} região(ões) selecionada(s)`
            : "Nenhuma região selecionada"}
        </Typography>
        <Tooltip
          title={
            !hasUnsavedChanges
              ? "Nenhuma alteração para salvar"
              : updateMutation.isLoading
                ? "Salvando suas alterações..."
                : "Clique para salvar as regiões selecionadas"
          }
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={updateMutation.isLoading || !hasUnsavedChanges}
              startIcon={
                updateMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
            >
              {updateMutation.isLoading ? "Salvando..." : "Salvar Regiões"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Snackbar para success e error com auto-hide de 3s */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirm Dialog para desmarcar todas as regiões */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => handleConfirmDialogClose(false)}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Desmarcar todas as regiões?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você está prestes a desmarcar todas as regiões de entrega. Isso
            significa que nenhuma região ficará selecionada. Tem certeza?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleConfirmDialogClose(false)}
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleConfirmDialogClose(true)}
            color="error"
            variant="contained"
          >
            Sim, desmarcar todas
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DeliveryRegionsMap;
